import { StratoApiError, PUBLIC_ERROR_MESSAGES } from "./apiErrors";
import { requestJson } from "./stratoClient";

export const ACCEPTED_FILE_EXTENSIONS = ["stl", "step", "stp", "3mf"];
export const ACCEPTED_REFERENCE_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];
export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_REFERENCE_IMAGE_BYTES = 10 * 1024 * 1024;
export const UPLOAD_SESSION_TOKEN_HEADER = "x-strato-upload-session-token";

const PART_CATEGORY_BY_LABEL = {
  Prototipo: "prototype",
  "Parte funzionale": "functional_part",
  "Pezzo estetico": "aesthetic_part",
  Ricambio: "replacement_part",
  "Supporto / staffa": "bracket_support",
  "Dima / attrezzatura": "jig_tooling",
  "Oggetto decorativo": "gadget_decorative",
  Altro: "other",
};

const USE_CONTEXT_BY_LABEL = {
  Interno: "indoor",
  Esterno: "outdoor",
  "Meccanico / funzionale": "mechanical_functional",
  Estetico: "aesthetic",
  "Test / prototipo": "test_prototype",
  "Montaggio / supporto": "mounting_support",
  Ricambio: "replacement",
  Altro: "other",
  "Non so, consigliatemi voi": "advise_me",
};

const MAIN_PRIORITY_BY_LABEL = {
  "Contenere il costo": "lowest_price",
  "Miglior aspetto estetico": "best_aesthetic",
  "Maggiore resistenza": "strength",
  "Maggiore precisione": "precision_detail",
  "Tempi rapidi": "fast_timing",
  "Equilibrio generale": "balanced",
  "Consigliatemi voi": "advise_me",
};

const COLOR_BY_LABEL = {
  "Pure White": "pure_white",
  "Absolute Black": "absolute_black",
  "Milky Pink": "milky_pink",
  Apricot: "apricot",
  "Baby Blue": "baby_blue",
};

const FINISH_BY_LABEL = {
  Standard: "standard",
  "Superficie più pulita possibile": "clean_surface",
  "Layer fine": "fine_layer",
  "Da definire in base al pezzo": "defined_by_part",
};

const URGENCY_BY_LABEL = {
  Standard: "standard",
  "Appena possibile": "asap",
  "Ho una data specifica": "specific_date",
  "Non urgente": "not_urgent",
};

export function createClientIdempotencyKey(prefix = "quote") {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}:${crypto.randomUUID()}`;
  }
  return `${prefix}:${Date.now()}:${Math.random().toString(36).slice(2, 12)}`;
}

export function getFileExtension(filename = "") {
  return String(filename).split(".").pop()?.toLowerCase() || "";
}

export function mimeForFilename(filename = "") {
  const ext = getFileExtension(filename);
  if (ext === "stl") return "model/stl";
  if (ext === "step" || ext === "stp") return "model/step";
  if (ext === "3mf") return "model/3mf";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  return "application/octet-stream";
}

export function validateSelectedFile(file, { attachmentType = "model_3d" } = {}) {
  if (!file) {
    return { ok: false, code: "validation_error", message: "Seleziona un file." };
  }

  const isReferenceImage = attachmentType === "reference_image";
  const acceptedExtensions = isReferenceImage ? ACCEPTED_REFERENCE_IMAGE_EXTENSIONS : ACCEPTED_FILE_EXTENSIONS;
  const maxBytes = isReferenceImage ? MAX_REFERENCE_IMAGE_BYTES : MAX_FILE_BYTES;
  const extension = getFileExtension(file.name);

  if (!acceptedExtensions.includes(extension)) {
    return {
      ok: false,
      code: "unsupported_file_type",
      message: isReferenceImage
        ? "Formato immagine non supportato. Usa JPG, JPEG, PNG o WebP."
        : PUBLIC_ERROR_MESSAGES.unsupported_file_type,
    };
  }

  if (isReferenceImage && file.type && !["image/jpeg", "image/png", "image/webp"].includes(file.type.toLowerCase())) {
    return {
      ok: false,
      code: "unsupported_file_type",
      message: "Formato immagine non supportato. Usa JPG, JPEG, PNG o WebP.",
    };
  }

  if (file.size > maxBytes) {
    return {
      ok: false,
      code: "file_too_large",
      message: isReferenceImage ? "L’immagine supera il limite di 10 MiB." : PUBLIC_ERROR_MESSAGES.file_too_large,
    };
  }

  return { ok: true };
}

export function createUploadSession() {
  return requestJson("/public/v1/upload-sessions", { method: "POST", body: {} });
}

export function createUploadIntent(uploadSessionId, uploadSessionToken, file, idempotencyKey, { attachmentType = "model_3d" } = {}) {
  return requestJson(`/public/v1/upload-sessions/${encodeURIComponent(uploadSessionId)}/upload-intent`, {
    method: "POST",
    headers: { [UPLOAD_SESSION_TOKEN_HEADER]: uploadSessionToken },
    body: {
      idempotencyKey,
      originalFilename: file.name,
      sizeBytes: file.size,
      mimeTypeReported: file.type || mimeForFilename(file.name),
      checksum: "",
      attachmentType,
    },
  });
}

export async function uploadFileToSignedUrl({
  uploadUrl,
  uploadHeaders = {},
  file,
  onProgress,
  transport = xhrUpload,
}) {
  return await transport({ uploadUrl, uploadHeaders, file, onProgress });
}

export function completeUpload(uploadSessionId, uploadSessionToken, fileId) {
  return requestJson(`/public/v1/upload-sessions/${encodeURIComponent(uploadSessionId)}/upload-complete`, {
    method: "POST",
    headers: { [UPLOAD_SESSION_TOKEN_HEADER]: uploadSessionToken },
    body: { fileId, checksum: "" },
  });
}

export function removeUpload(uploadSessionId, uploadSessionToken, fileId) {
  return requestJson(
    `/public/v1/upload-sessions/${encodeURIComponent(uploadSessionId)}/files/${encodeURIComponent(fileId)}`,
    {
      method: "DELETE",
      headers: { [UPLOAD_SESSION_TOKEN_HEADER]: uploadSessionToken },
      body: {},
    },
  );
}

export function submitQuote(payload, { uploadSessionToken } = {}) {
  const headers = uploadSessionToken ? { [UPLOAD_SESSION_TOKEN_HEADER]: uploadSessionToken } : {};
  return requestJson("/public/v1/quotes", { method: "POST", headers, body: payload, timeoutMs: 30000 });
}

export function buildQuotePayload(data, { idempotencyKey } = {}) {
  const isFilePath = data.start === "file";
  const selectedColor = data.customColor ? null : data.selectedColorName || data.colorName;
  const color = data.customColor ? "other" : COLOR_BY_LABEL[selectedColor] || null;
  const customerName = [data.firstName, data.lastName].map((part) => String(part || "").trim()).filter(Boolean).join(" ");
  const quantity = Math.max(1, Number.parseInt(data.quantity || 1, 10));
  const finalNotes = buildFinalNotes(data);

  const payload = {
    idempotencyKey,
    pathType: isFilePath ? "file" : "no-file",
    projectStartType: isFilePath ? "file" : "no_file",
    fileReferenceMode: isFilePath ? "uploaded_file" : "reference_optional",
    pieceDescription: isFilePath ? "" : cleanLong(data.description),
    usageDescription: usageDescription(data),
    quantity: `${quantity} pezzi`,
    priority: cleanText(data.goal),
    customer: {
      name: customerName,
      email: cleanText(data.email).toLowerCase(),
      phone: cleanText(data.phone),
      company: cleanText(data.company),
      notes: "",
    },
    privacyAccepted: data.acceptTerms === true,
    termsAccepted: data.acceptTerms === true,
    safetyAccepted: data.acceptLawful === true,
    marketingAccepted: false,
    honeypot: cleanText(data.hp),
    partCategory: PART_CATEGORY_BY_LABEL[data.pieceType] || null,
    partCategoryOther: data.pieceType === "Altro" ? cleanText(data.pieceTypeOther) : "",
    useContext: USE_CONTEXT_BY_LABEL[data.use] || null,
    mainPriority: MAIN_PRIORITY_BY_LABEL[data.goal] || null,
    preferredTechnology: "fdm",
    preferredMaterial: materialEnum(data.materialType),
    preferredMaterialOther: data.materialType === "custom" ? cleanText(data.materialRequest) : "",
    desiredColor: color,
    desiredColorOther: data.customColor ? cleanText(data.colorRequest) : "",
    desiredFinish: FINISH_BY_LABEL[data.finish] || null,
    requestedQuantityMode: "custom",
    requestedQuantityCustom: quantity,
    urgency: URGENCY_BY_LABEL[data.urgency] || null,
    urgencyDate: data.urgency === "Ho una data specifica" ? cleanText(data.desiredDate) : "",
    finalNotes,
  };

  if (isFilePath) {
    payload.uploadSessionId = data.uploadSessionId || "";
  } else if (data.fileStatus === "done" && data.uploadSessionId) {
    payload.uploadSessionId = data.uploadSessionId || "";
  }

  return payload;
}

function xhrUpload({ uploadUrl, uploadHeaders, file, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    Object.entries(uploadHeaders || {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onProgress === "function") {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ ok: true, status: xhr.status });
      } else {
        reject(
          new StratoApiError({
            code: "upload_not_found",
            message: PUBLIC_ERROR_MESSAGES.upload_not_found,
            status: xhr.status,
            retryable: true,
          }),
        );
      }
    };
    xhr.onerror = () =>
      reject(
        new StratoApiError({
          code: "network_error",
          message: PUBLIC_ERROR_MESSAGES.network_error,
          retryable: true,
        }),
      );
    xhr.onabort = () =>
      reject(
        new StratoApiError({
          code: "network_error",
          message: PUBLIC_ERROR_MESSAGES.network_error,
          retryable: true,
        }),
      );
    xhr.send(file);
  });
}

function materialEnum(materialType) {
  if (materialType === "pla") return "pla";
  if (materialType === "advice") return "advise_me";
  if (materialType === "custom") return "other_on_review";
  return null;
}

function usageDescription(data) {
  if (data.use === "Altro" && data.useOther) return cleanLong(data.useOther);
  return cleanLong(data.use || data.goal || data.description || "Uso finale da valutare");
}

function buildFinalNotes(data) {
  const notes = [];
  if (data.notes) notes.push(cleanLong(data.notes));
  if (data.materialType === "custom" && data.materialRequest) {
    notes.push(`Materiale richiesto: ${cleanText(data.materialRequest)}`);
  }
  return notes.filter(Boolean).join("\n\n");
}

function cleanText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function cleanLong(value) {
  return String(value || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
}
